import { useRef, useState } from 'react';
import { Loader2, Upload as UploadIcon, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFileUpload, type UploadPurpose, type UploadedFile } from './use-file-upload';

interface UploadDropzoneProps {
  purpose: UploadPurpose;
  onUploaded: (file: UploadedFile) => void | Promise<void>;
  /** Lista de MIME types aceitos. Default: imagens + PDF. */
  accept?: string;
  /** Tamanho máximo em bytes. Default: 10 MB. */
  maxSizeBytes?: number;
  /** Label customizado. */
  label?: string;
  disabled?: boolean;
  className?: string;
}

const DEFAULT_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const DEFAULT_MAX = 10 * 1024 * 1024;

/**
 * Área de upload com drag-drop + click-to-select + barra de progresso.
 * Usa o hook `useFileUpload` para orquestrar presign → PUT → commit.
 */
export function UploadDropzone({
  purpose,
  onUploaded,
  accept = DEFAULT_ACCEPT,
  maxSizeBytes = DEFAULT_MAX,
  label = 'Arraste um arquivo aqui ou clique para selecionar',
  disabled,
  className,
}: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const { state, upload, reset } = useFileUpload();

  const isBusy =
    state.phase === 'presigning' || state.phase === 'uploading' || state.phase === 'committing';

  const handleFile = async (file: File) => {
    setLocalError(null);
    if (file.size > maxSizeBytes) {
      setLocalError(`Arquivo muito grande. Máximo ${Math.round(maxSizeBytes / 1024 / 1024)} MB.`);
      return;
    }
    if (accept && !accept.split(',').some((t) => file.type === t.trim())) {
      setLocalError(`Tipo de arquivo não suportado: ${file.type || 'desconhecido'}`);
      return;
    }
    try {
      const uploaded = await upload(file, purpose);
      await onUploaded(uploaded);
      // Reseta para permitir novo upload sem mostrar "concluído" infinitamente
      setTimeout(() => reset(), 800);
    } catch {
      // `useFileUpload` já põe a mensagem em state.error
    }
  };

  const onPick = () => {
    if (disabled || isBusy) return;
    inputRef.current?.click();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isBusy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const progressLabel = (() => {
    switch (state.phase) {
      case 'presigning':
        return 'Preparando...';
      case 'uploading':
        return `Enviando ${state.progress}%`;
      case 'committing':
        return 'Finalizando...';
      case 'done':
        return 'Concluído';
      default:
        return null;
    }
  })();

  return (
    <div className={cn('space-y-2', className)}>
      <button
        type="button"
        onClick={onPick}
        onDragOver={(e) => {
          e.preventDefault();
          if (!isBusy) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        disabled={disabled || isBusy}
        className={cn(
          'group flex w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 transition-all',
          'bg-muted/30 text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-60',
          isDragging
            ? 'border-primary bg-primary/5 text-primary'
            : 'border-border hover:border-primary/50 hover:bg-muted/50',
        )}
      >
        <div
          className={cn(
            'flex size-12 items-center justify-center rounded-full bg-background transition-colors',
            isDragging ? 'text-primary' : 'group-hover:text-primary',
          )}
        >
          {isBusy ? (
            <Loader2 className="size-6 animate-spin" />
          ) : (
            <UploadIcon className="size-6" />
          )}
        </div>
        <div className="space-y-1 text-center">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-xs">
            {accept.includes('pdf') ? 'Imagens (JPG, PNG, WEBP) ou PDF' : 'Imagens'}
            {' · até '}
            {Math.round(maxSizeBytes / 1024 / 1024)} MB
          </p>
          {progressLabel && (
            <p className="pt-2 text-xs font-medium text-primary">{progressLabel}</p>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </button>

      {state.phase === 'uploading' && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${state.progress}%` }}
          />
        </div>
      )}

      {(localError || state.error) && (
        <p className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <FileText className="size-3.5" />
          {localError ?? state.error}
        </p>
      )}
    </div>
  );
}
